import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { showAlert } from 'actions/app';
import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import { SchemaStatuses } from 'constants/schemaStatuses';
import { useUserContext } from 'providers/UserProvider';
import { getToken } from 'services/Authentication';
import strings from 'strings';
import { EnvApiUrls } from 'UI/constants/defaults';
import { FormSchema } from 'UI/constants/entityTypes';
import { UIStatus } from 'UI/constants/status';
import { getDataSheetCount } from 'UI/pages/NewCandidates/CandidateDataSheetDrawer/CandidateDataSheetDrawer.utils';
import { getErrorMessage } from 'UI/utils';
import { cleanCDSLocations } from 'utils';

import { CANDIDATE_DATA_SHEET_QUESTIONS_PATHS } from '../EditCandidateDataSheet.constants';
import { saveCandidateDataSheet } from '../EditCandidateDataSheet.services';
import { createDataToSend } from '../EditCandidateDataSheet.utils';

const {
  complete: { success: completeSuccess, error: completeError },
  draft: { success: draftSuccess, error: draftError },
  error: fieldsError
} = strings.candidates.editDataSheet.uiMessages;

/**
 * @param {Object} props
 * @param {Object} props.blueSheet
 * @param {Object} props.dataSheet
 * @param {Function} props.updateDataSheet
 * @param {Function} props.toggleDraftDialog
 */
const useCandidateDataSheetForm = ({
  blueSheet,
  dataSheet,
  updateDataSheet,
  toggleDraftDialog
}) => {
  const dispatch = useDispatch();
  const [user] = useUserContext();
  const [status, setStatus] = useState(UIStatus.Default);
  const [formData, setFormData] = useState();
  const saveButtonRef = useRef(null);

  const counts = getDataSheetCount(
    formData,
    CANDIDATE_DATA_SHEET_QUESTIONS_PATHS,
    FormSchema.CandidateDataSheetFull
  );

  useEffect(() => {
    if (!dataSheet) return;

    setFormData(dataSheet.data);
  }, [dataSheet]);

  const handleOnChange = ({ formData: newFormData }) => {
    setFormData(newFormData);
  };

  const commonStyledFormProps = {
    showErrorList: false,
    noHtml5Validate: true,
    focusOnFirstError: true,
    submitRef: saveButtonRef,
    httpOptions: {
      headers: {
        Authorization: `Bearer ${getToken()}`,
        ...(EnvApiUrls.SEND_API_KEY && { 'x-api-key': EnvApiUrls.API_KEY_AWS })
      }
    },
    baseUrl: EnvApiUrls.MAIN.replace('api/v1', ''),
    secondaryBaseUrl: `${EnvApiUrls.API_URL_AWS}/`
  };

  const getHandleOnSubmit =
    ({ candidate, schemaVersion, navigateToProfile }) =>
    async ({ formData: newFormData }) => {
      setStatus(UIStatus.Saving);
      setFormData(newFormData);

      const dataToSend = createDataToSend({
        formData: cleanCDSLocations(newFormData),
        candidate,
        schemaVersion,
        user,
        blueSheetId: blueSheet?.id,
        count: counts,
        ...(dataSheet && { dataSheetId: dataSheet.id })
      });

      const toastBody = candidate.personalInformation.full_name;

      try {
        const response = await saveCandidateDataSheet(dataToSend);

        setStatus(UIStatus.IDLE);

        if ([HTTPStatusCodes.BadRequest, HTTPStatusCodes.Unauthorized].includes(response.status)) {
          dispatch(
            showAlert({
              severity: UIStatus.Error,
              title:
                response.data.progress.key === SchemaStatuses.Completed
                  ? completeError
                  : draftError,
              body: toastBody
            })
          );
          return;
        }

        updateDataSheet(response.data);

        dispatch(
          showAlert({
            severity: UIStatus.Success,
            title:
              response.data.progress.key === SchemaStatuses.Completed
                ? completeSuccess
                : draftSuccess,
            body: toastBody
          })
        );

        navigateToProfile();
      } catch (error) {
        setStatus(UIStatus.Error);
        dispatch(
          showAlert({
            severity: UIStatus.Error,
            title: strings.candidates.editDataSheet.title,
            body: getErrorMessage(error)
          })
        );
      }
    };

  const handleOnSave = () => {
    saveButtonRef.current.click();
    toggleDraftDialog(false)();
  };

  const getHandleOnError =
    ({ candidate }) =>
    errors => {
      if (errors && errors.length === 0) return;

      const toastBody = candidate.personalInformation.full_name;

      dispatch(
        showAlert({
          severity: UIStatus.Error,
          title: fieldsError,
          body: toastBody
        })
      );

      toggleDraftDialog(false)();
    };

  return {
    formData,
    formStatus: status,
    commonStyledFormProps,
    canSave:
      dataSheet?.progress.key !== SchemaStatuses.Completed ||
      (dataSheet?.progress.key === SchemaStatuses.Completed && counts.total === counts.answered),
    handleOnChange,
    getHandleOnSubmit,
    handleOnSave,
    getHandleOnError
  };
};

export default useCandidateDataSheetForm;
